import { useState, useEffect } from "react";
import { supabase, AssociationActivityRow } from "../../../lib/supabase";
import ImageUploader from "../../../components/base/ImageUploader";
import BannerUploader, { FocalPoint } from "../../../components/base/BannerUploader";
import RichTextEditor from "../../../components/base/RichTextEditor";

type Lang = "vi" | "kr";
interface Props { lang: Lang; }

const CATEGORIES = [
  { id: "news", vi: "Tin tức", kr: "뉴스" },
  { id: "event", vi: "Sự kiện", kr: "이벤트" },
  { id: "cooperation", vi: "Hợp tác", kr: "협력" },
  { id: "seminar", vi: "Hội thảo", kr: "세미나" },
  { id: "mou", vi: "MOU / Ký kết", kr: "MOU / 협약" },
  { id: "exhibition", vi: "Triển lãm", kr: "전시회" },
  { id: "other", vi: "Khác", kr: "기타" },
];

const emptyForm = (): Omit<AssociationActivityRow, "id" | "created_at" | "updated_at"> => ({
  title: "",
  title_kr: "",
  subtitle: "",
  content: "",
  image_url: "",
  gallery: [],
  link_url: "",
  category: "news",
  tags: [],
  author: "",
  is_featured: false,
  is_published: true,
  published_at: new Date().toISOString().slice(0, 16),
  event_date: "",
  event_location: "",
});

interface ActivityFormProps {
  lang: Lang;
  editData?: AssociationActivityRow | null;
  onClose: () => void;
  onSuccess: () => void;
}

const ActivityFormModal: React.FC<ActivityFormProps> = ({ lang, editData, onClose, onSuccess }) => {
  const t = (vi: string, kr: string) => (lang === "vi" ? vi : kr);
  const [form, setForm] = useState(emptyForm());
  const [coverImages, setCoverImages] = useState<string[]>([]);
  const [coverFocal, setCoverFocal] = useState<FocalPoint>({ x: 50, y: 50 });
  const [focalX, setFocalX] = useState(50);
  const [focalY, setFocalY] = useState(50);
  const [galleryImages, setGalleryImages] = useState<string[]>([]);
  const [tagsInput, setTagsInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState<"basic" | "media" | "content">("basic");

  useEffect(() => {
    if (editData) {
      setForm({
        title: editData.title ?? "",
        title_kr: editData.title_kr ?? "",
        subtitle: editData.subtitle ?? "",
        content: editData.content ?? "",
        image_url: editData.image_url ?? "",
        gallery: editData.gallery ?? [],
        link_url: editData.link_url ?? "",
        category: editData.category ?? "news",
        tags: editData.tags ?? [],
        author: editData.author ?? "",
        is_featured: editData.is_featured ?? false,
        is_published: editData.is_published ?? true,
        published_at: editData.published_at ? editData.published_at.slice(0, 16) : new Date().toISOString().slice(0, 16),
        event_date: editData.event_date ?? "",
        event_location: editData.event_location ?? "",
      });
      if (editData.image_url) setCoverImages([editData.image_url]);
      if (editData.gallery && editData.gallery.length > 0) setGalleryImages(editData.gallery);
      const fx = editData.focal_point_x ?? 50;
      const fy = editData.focal_point_y ?? 50;
      setCoverFocal({ x: fx, y: fy });
      setFocalX(fx);
      setFocalY(fy);
      setTagsInput((editData.tags ?? []).join(", "));
    }
  }, [editData]);

  const set = (field: string, val: unknown) => setForm((prev) => ({ ...prev, [field]: val }));

  const handleSave = async () => {
    if (!form.title.trim()) { setError(t("Vui lòng nhập tiêu đề!", "제목을 입력해주세요!")); return; }
    setSaving(true);
    setError("");

    const tags = tagsInput.split(",").map((s) => s.trim()).filter(Boolean);
    const payload = {
      ...form,
      image_url: coverImages[0] ?? form.image_url ?? "",
      gallery: galleryImages,
      tags,
      focal_point_x: focalX,
      focal_point_y: focalY,
      published_at: form.published_at ? new Date(form.published_at).toISOString() : new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    let err;
    if (editData?.id) {
      const { error: e } = await supabase.from("association_activities").update(payload).eq("id", editData.id);
      err = e;
    } else {
      const { error: e } = await supabase.from("association_activities").insert(payload);
      err = e;
    }

    setSaving(false);
    if (err) { setError(err.message); return; }
    onSuccess();
  };

  const inputCls = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:border-[#004A99] outline-none bg-white";
  const labelCls = "block text-xs font-semibold text-gray-600 mb-1";

  const tabs = [
    { id: "basic" as const, label: t("Thông tin", "기본 정보"), icon: "ri-information-line" },
    { id: "media" as const, label: t("Hình ảnh", "이미지"), icon: "ri-image-line" },
    { id: "content" as const, label: t("Nội dung", "콘텐츠"), icon: "ri-article-line" },
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center">
              <i className="ri-newspaper-line text-amber-700" />
            </div>
            <h2 className="font-bold text-gray-800">
              {editData ? t("Chỉnh sửa bài viết", "게시물 수정") : t("Thêm bài viết mới", "새 게시물 추가")}
            </h2>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-gray-500" />
          </button>
        </div>

        <div className="flex border-b border-gray-100 flex-shrink-0 px-6">
          {tabs.map((tab) => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-3 text-xs font-semibold border-b-2 transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === tab.id ? "border-[#004A99] text-[#004A99]" : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              <i className={tab.icon} />{tab.label}
            </button>
          ))}
        </div>

        <div className="p-6 overflow-y-auto flex-1 space-y-4">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg px-3 py-2">{error}</div>}

          {activeTab === "basic" && (
            <>
              <div>
                <label className={labelCls}>{t("Tiêu đề (VI) *", "제목 (VI) *")}</label>
                <input className={inputCls} value={form.title} onChange={(e) => set("title", e.target.value)} placeholder={t("Nhập tiêu đề bài viết...", "게시물 제목 입력...")} />
              </div>
              <div>
                <label className={labelCls}>{t("Tiêu đề (KR)", "제목 (KR)")}</label>
                <input className={inputCls} value={form.title_kr ?? ""} onChange={(e) => set("title_kr", e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>{t("Mô tả ngắn / Subtitle", "부제목 / 설명")}</label>
                <input className={inputCls} value={form.subtitle ?? ""} onChange={(e) => set("subtitle", e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>{t("Danh mục", "카테고리")}</label>
                  <select className={inputCls} value={form.category ?? "news"} onChange={(e) => set("category", e.target.value)}>
                    {CATEGORIES.map((c) => (
                      <option key={c.id} value={c.id}>{lang === "vi" ? c.vi : c.kr}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>{t("Tác giả", "작성자")}</label>
                  <input className={inputCls} value={form.author ?? ""} onChange={(e) => set("author", e.target.value)} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>{t("Ngày sự kiện (tùy chọn)", "행사 날짜 (선택)")}</label>
                  <input className={inputCls} type="text" value={form.event_date ?? ""} onChange={(e) => set("event_date", e.target.value)} placeholder="VD: 15/03/2025" />
                </div>
                <div>
                  <label className={labelCls}>{t("Địa điểm (tùy chọn)", "장소 (선택)")}</label>
                  <input className={inputCls} value={form.event_location ?? ""} onChange={(e) => set("event_location", e.target.value)} placeholder={t("VD: Hà Nội, Việt Nam", "예: 서울, 한국")} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>{t("Ngày đăng", "발행일")}</label>
                  <input type="datetime-local" className={inputCls} value={form.published_at ?? ""} onChange={(e) => set("published_at", e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>{t("Link gốc (tùy chọn)", "원본 링크 (선택)")}</label>
                  <input className={inputCls} value={form.link_url ?? ""} onChange={(e) => set("link_url", e.target.value)} placeholder="https://..." />
                </div>
              </div>
              <div>
                <label className={labelCls}>{t("Tags (cách bằng dấu phẩy)", "태그 (쉼표로 구분)")}</label>
                <input className={inputCls} value={tagsInput} onChange={(e) => setTagsInput(e.target.value)} placeholder="Hàn Quốc, hợp tác, xuất khẩu" />
              </div>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.is_published ?? true} onChange={(e) => set("is_published", e.target.checked)} className="w-4 h-4 rounded" />
                  <span className="text-xs font-semibold text-gray-700">{t("Đăng công khai", "공개 게시")}</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.is_featured ?? false} onChange={(e) => set("is_featured", e.target.checked)} className="w-4 h-4 rounded" />
                  <span className="text-xs font-semibold text-gray-700">{t("Bài nổi bật", "추천 게시물")}</span>
                </label>
              </div>
            </>
          )}

          {activeTab === "media" && (
            <div className="space-y-6">
              <BannerUploader
                imageUrl={coverImages[0] ?? ""}
                focalPoint={coverFocal}
                onChange={(url, focal) => {
                  setCoverImages(url ? [url] : []);
                  setCoverFocal(focal);
                  setFocalX(focal.x);
                  setFocalY(focal.y);
                  set("image_url", url);
                }}
                aspectRatio={16 / 9}
                folder="association"
                label={t("Ảnh bìa bài viết (16:9 · Khuyến nghị >1920px)", "커버 이미지 (16:9 · 1920px 이상 권장)")}
                previewLabel={t("Xem trước ảnh bìa", "커버 미리보기")}
              />
              {coverImages[0] && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-700">
                  <i className="ri-focus-3-line mr-1" />
                  {t(`Tâm điểm: X=${focalX.toFixed(0)}%, Y=${focalY.toFixed(0)}% — Ảnh sẽ hiển thị đúng vị trí này trên trang web`, `초점: X=${focalX.toFixed(0)}%, Y=${focalY.toFixed(0)}% — 웹사이트에서 이 위치로 표시됩니다`)}
                </div>
              )}
              <ImageUploader
                images={galleryImages}
                onChange={setGalleryImages}
                maxImages={10}
                folder="association"
                label={t("Thư viện ảnh (tối đa 10 ảnh)", "이미지 갤러리 (최대 10장)")}
              />
            </div>
          )}

          {activeTab === "content" && (
            <div className="space-y-3">
              <div className="bg-amber-50 rounded-xl p-3 border border-amber-200">
                <p className="text-xs text-amber-700">{t("Soạn thảo nội dung bài viết với đầy đủ định dạng: chữ đậm, nghiêng, tiêu đề, danh sách và chèn ảnh trực tiếp.", "서식, 이미지 삽입 등 전체 편집 기능을 사용하여 게시물 내용을 작성하세요.")}</p>
              </div>
              <RichTextEditor
                value={form.content ?? ""}
                onChange={(html) => set("content", html)}
                placeholder={t("Nhập nội dung chi tiết bài viết... Bạn có thể in đậm, nghiêng, chèn ảnh trực tiếp vào bài.", "게시물의 상세 내용을 입력하세요...")}
                minHeight={360}
                folder="association"
                label={t("Nội dung bài viết", "게시물 내용")}
              />
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between flex-shrink-0">
          <p className="text-xs text-gray-400">{coverImages.length > 0 ? t("✓ Có ảnh bìa", "✓ 커버 이미지 있음") : t("Chưa có ảnh bìa", "커버 이미지 없음")}</p>
          <div className="flex gap-3">
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg cursor-pointer whitespace-nowrap transition-colors">{t("Hủy", "취소")}</button>
            <button onClick={handleSave} disabled={saving} className="px-5 py-2 bg-[#004A99] hover:bg-[#003a7a] text-white text-sm font-semibold rounded-lg cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center gap-2 transition-colors">
              {saving && <i className="ri-loader-4-line animate-spin" />}
              {t("Lưu bài viết", "저장")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const AssociationManager: React.FC<Props> = ({ lang }) => {
  const t = (vi: string, kr: string) => (lang === "vi" ? vi : kr);
  const [activities, setActivities] = useState<AssociationActivityRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState<AssociationActivityRow | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const fetchActivities = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("association_activities")
      .select("*")
      .order("published_at", { ascending: false });
    setActivities((data as AssociationActivityRow[]) ?? []);
    setLoading(false);
  };

  useEffect(() => { fetchActivities(); }, []);

  const handleDelete = async (id: string) => {
    if (!window.confirm(t("Xác nhận xóa bài viết này?", "이 게시물을 삭제하시겠습니까?"))) return;
    setDeletingId(id);
    await supabase.from("association_activities").delete().eq("id", id);
    setDeletingId(null);
    fetchActivities();
  };

  const handleEdit = (item: AssociationActivityRow) => {
    setEditItem(item);
    setShowForm(true);
  };

  const togglePublish = async (item: AssociationActivityRow) => {
    await supabase.from("association_activities").update({ is_published: !item.is_published }).eq("id", item.id);
    fetchActivities();
  };

  const filtered = activities.filter((a) => {
    const q = searchQuery.toLowerCase();
    const matchQ = !q || a.title.toLowerCase().includes(q) || (a.subtitle ?? "").toLowerCase().includes(q);
    const matchCat = !filterCategory || a.category === filterCategory;
    return matchQ && matchCat;
  });

  const getCatLabel = (cat: string) => {
    const found = CATEGORIES.find((c) => c.id === cat);
    return found ? (lang === "vi" ? found.vi : found.kr) : cat;
  };

  return (
    <div>
      {showForm && (
        <ActivityFormModal
          lang={lang}
          editData={editItem}
          onClose={() => { setShowForm(false); setEditItem(null); }}
          onSuccess={() => { setShowForm(false); setEditItem(null); fetchActivities(); }}
        />
      )}

      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-800">{t("Hoạt Động Hiệp Hội", "협회 활동")}</h1>
          <p className="text-sm text-gray-500 mt-1">{t("Quản lý bài viết và sự kiện của Hiệp Hội", "협회 게시물 및 이벤트 관리")}</p>
        </div>
        <button
          onClick={() => { setEditItem(null); setShowForm(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-[#004A99] text-white text-sm font-semibold rounded-xl hover:bg-[#003a7a] transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line" />
          {t("Thêm bài viết", "게시물 추가")}
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {[
          { label: t("Tổng bài viết", "전체 게시물"), value: activities.length, color: "bg-[#004A99]", icon: "ri-newspaper-line" },
          { label: t("Đã đăng", "공개"), value: activities.filter((a) => a.is_published).length, color: "bg-green-600", icon: "ri-eye-line" },
          { label: t("Bài nổi bật", "추천"), value: activities.filter((a) => a.is_featured).length, color: "bg-amber-500", icon: "ri-star-line" },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-xl border border-gray-200 p-4">
            <div className={`w-8 h-8 ${s.color} rounded-lg flex items-center justify-center mb-2`}>
              <i className={`${s.icon} text-white text-sm`} />
            </div>
            <p className="text-xl font-black text-gray-800">{s.value}</p>
            <p className="text-xs text-gray-400">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
          <input
            type="text"
            placeholder={t("Tìm kiếm...", "검색...")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#004A99]"
          />
        </div>
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#004A99] cursor-pointer"
        >
          <option value="">{t("Tất cả danh mục", "전체 카테고리")}</option>
          {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{lang === "vi" ? c.vi : c.kr}</option>)}
        </select>
      </div>

      {/* List */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <i className="ri-loader-4-line animate-spin text-2xl text-[#004A99]" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <i className="ri-newspaper-line text-3xl text-gray-300 block mb-3" />
            <p className="text-sm text-gray-400">{t("Chưa có bài viết nào.", "게시물이 없습니다.")}</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filtered.map((item) => (
              <div key={item.id} className="flex items-center gap-4 p-4 hover:bg-[#F8F9FA] transition-colors">
                {/* Cover */}
                <div className="w-16 h-16 flex-shrink-0 rounded-xl overflow-hidden bg-gray-100">
                  {item.image_url ? (
                    <img src={item.image_url} alt={item.title} className="w-full h-full object-cover object-top" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <i className="ri-image-line text-gray-300 text-xl" />
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold whitespace-nowrap ${
                      item.is_published ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
                    }`}>
                      {item.is_published ? (lang === "vi" ? "Đã đăng" : "공개") : (lang === "vi" ? "Nháp" : "비공개")}
                    </span>
                    {item.is_featured && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold bg-amber-100 text-amber-700 whitespace-nowrap">
                        {lang === "vi" ? "Nổi bật" : "추천"}
                      </span>
                    )}
                    <span className="text-[10px] text-gray-400 whitespace-nowrap">{getCatLabel(item.category ?? "")}</span>
                  </div>
                  <p className="text-sm font-semibold text-gray-800 truncate">{item.title}</p>
                  {item.subtitle && <p className="text-xs text-gray-500 truncate">{item.subtitle}</p>}
                  <p className="text-[10px] text-gray-400 mt-0.5">
                    {item.published_at ? new Date(item.published_at).toLocaleDateString("vi-VN") : ""}
                    {item.event_date && ` · ${item.event_date}`}
                    {item.event_location && ` · ${item.event_location}`}
                  </p>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => togglePublish(item)}
                    title={item.is_published ? t("Ẩn bài", "비공개로 변경") : t("Đăng bài", "공개로 변경")}
                    className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                      item.is_published ? "bg-green-50 text-green-600 hover:bg-green-100" : "bg-gray-50 text-gray-400 hover:bg-gray-100"
                    }`}
                  >
                    <i className={item.is_published ? "ri-eye-line" : "ri-eye-off-line"} />
                  </button>
                  <button
                    onClick={() => handleEdit(item)}
                    className="w-8 h-8 flex items-center justify-center bg-[#004A99]/10 text-[#004A99] rounded-lg hover:bg-[#004A99]/20 transition-colors cursor-pointer"
                  >
                    <i className="ri-edit-line text-sm" />
                  </button>
                  <button
                    onClick={() => handleDelete(item.id)}
                    disabled={deletingId === item.id}
                    className="w-8 h-8 flex items-center justify-center bg-red-50 text-red-500 rounded-lg hover:bg-red-100 transition-colors cursor-pointer disabled:opacity-50"
                  >
                    {deletingId === item.id ? <i className="ri-loader-4-line animate-spin text-sm" /> : <i className="ri-delete-bin-line text-sm" />}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AssociationManager;
